/* eslint-disable camelcase */
import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import history from 'services/history';
import { gql, useQuery, useMutation } from '@apollo/client';

// app modules: actions
import addItemToCollection from 'actions/user/addItemToCollection';

// app modules: components
import ActionSheet from 'components/ActionSheet';
import AddToCollection from 'components/AddToCollection';
import DialogWithCloseButton from 'components/DialogWithCloseButton';
import NewCollectionForm from 'components/Collection/NewCollectionForm';

import { COLLECTION_SORT_OPTIONS } from '../../constants';

import { SortingComponentDesktop, SortingComponentMobile } from './SortingComponents';

const GQL = {
  GET_SETTINGS: gql`
    query GetCollectionPickerSortOrderSetting {
      User {
        id
        settings {
          id
          collection_picker_sort_order
        }
      }
    }
  `,
  SAVE_SETTINGS: gql`
    mutation UpdateCollectionPickerSortOrderSettings($settings: SettingsInput) {
      UpdateUserSettings2(settings: $settings) {
        id
        collection_picker_sort_order
      }
    }
  `,
};

function AddToCollectionWrapper({ itemId, itemType, onClose }) {
  const dispatch = useDispatch();
  const userId = useSelector(state => state.user.id);
  const isLoggedIn = useSelector(state => state.user.logged_in);
  const displayType = useSelector(state => state.dimensions.displayType);
  const collections = useSelector(state => state.user.collections);
  const availableCollections = Object.values(collections).filter(collection => collection);
  const hasCollections = availableCollections.length > 0;
  const [showNewCollectionDialog, setShowNewCollectionDialog] = useState(!hasCollections);
  const [isOpen, setIsOpen] = useState(hasCollections);
  const [setCollectionPickerSortOrder] = useMutation(GQL.SAVE_SETTINGS);
  const { data, loading } = useQuery(GQL.GET_SETTINGS, {
    skip: !isLoggedIn || userId <= 0,
    ssr: false,
    variables: {
      _authenticated: true,
    },
    fetchPolicy: 'cache-and-network',
  });

  const currentSortingOption = COLLECTION_SORT_OPTIONS.find(
    option => option.key === (data?.User?.settings?.collection_picker_sort_order || 'created_at'),
  );

  useEffect(
    () => {
      if (!isLoggedIn) {
        history.push('/subscribe', {
          returnTo: history.location,
        });
      }
    },
    [isLoggedIn],
  );

  const handleSortingOptionChange = option => {
    setCollectionPickerSortOrder({
      variables: { settings: { collection_picker_sort_order: option.key } },
      optimisticResponse: {
        UpdateUserSettings2: {
          __typename: 'Settings',
          id: userId,
          collection_picker_sort_order: option.key,
        },
      },
    });
  };

  const handleOpenNewCollectionModal = () => {
    setIsOpen(false);
    setShowNewCollectionDialog(true);
  };

  const addItemToCollectionFn = async id => {
    await dispatch(addItemToCollection({ id, itemId, itemType }));
    onClose();
  };

  const renderCollectionActionSheetItems = () => (
    <AddToCollection
      handleSortingOptionChange={handleSortingOptionChange}
      currentSortingOption={currentSortingOption}
      handleOpenNewCollectionModal={handleOpenNewCollectionModal}
      SortingComponent={SortingComponentMobile}
      collections={availableCollections}
      addItemToCollectionFn={addItemToCollectionFn}
    />
  );

  const hideShowNewCollectionForm = () => {
    setShowNewCollectionDialog(false);
    if (hasCollections) {
      setIsOpen(hasCollections);
    }
    onClose();
  };

  const renderNewCollectionActionSheet = () => (
    <NewCollectionForm itemId={itemId} itemType={itemType} onClose={hideShowNewCollectionForm} />
  );

  if (loading || !isLoggedIn) {
    return null;
  }

  const useActionSheet = displayType === 'mobile';
  return (
    <Fragment>
      {useActionSheet && (
        <ActionSheet
          withCloseButton
          onClose={hideShowNewCollectionForm}
          itemRenderer={renderNewCollectionActionSheet}
          visible={showNewCollectionDialog}
        />
      )}

      {!useActionSheet && (
        <DialogWithCloseButton isOpen={showNewCollectionDialog} onClose={hideShowNewCollectionForm}>
          <NewCollectionForm
            itemId={itemId}
            itemType={itemType}
            onClose={hideShowNewCollectionForm}
          />
        </DialogWithCloseButton>
      )}

      {useActionSheet && (
        <ActionSheet
          withCloseButton
          onClose={onClose}
          itemRenderer={renderCollectionActionSheetItems}
          heightVh={availableCollections.length > 0 && 50}
          visible={isOpen}
          swipeable={false}
        />
      )}
      {!useActionSheet && (
        <DialogWithCloseButton isOpen={isOpen} onClose={onClose}>
          <AddToCollection
            SortingComponent={SortingComponentDesktop}
            collections={availableCollections}
            addItemToCollectionFn={addItemToCollectionFn}
            handleSortingOptionChange={handleSortingOptionChange}
            currentSortingOption={currentSortingOption}
            handleOpenNewCollectionModal={handleOpenNewCollectionModal}
          />
        </DialogWithCloseButton>
      )}
    </Fragment>
  );
}

AddToCollectionWrapper.propTypes = {
  onClose: PropTypes.func,
  itemId: PropTypes.string.isRequired,
  itemType: PropTypes.string.isRequired,
};

AddToCollectionWrapper.defaultProps = {
  onClose: () => {},
};

export default AddToCollectionWrapper;
